import classNames from 'classnames';
import {debounce} from 'lodash';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import lightFilterNodeStyles from './LightFilterNodeStyles.less';
import {FilterConfig, useFormatters} from './LightFormatter';
import {FilterNodeTypeFulltext, FilterNodeTypeTextContainer} from 'src/js/containers/filtering';
import {DynamicFilter, FILTER_TYPE_FULLTEXT, FILTER_TYPE_TEXT, Filter} from 'src/js/lib/filtering';
import {FilterNode} from 'src/js/model/filters_config';

type Props = {
  config: FilterConfig;
  value: string | null | undefined;
  onChange?: (newValue: string | null) => void;
  isTextArea?: boolean;
  wrapperWidth?: string;
  inputWidth?: string;
  /** Icon shown at the end of the field while it is empty; the magnifier by default, `numbersIcon` for a numeric value. */
  iconName?: string;
};
export const LightTextFilter: React.FC<Props> = (props) => {
  const {config: _config, value, onChange, isTextArea, wrapperWidth, inputWidth, iconName} = props;
  const filterType = isTextArea ? FILTER_TYPE_FULLTEXT : FILTER_TYPE_TEXT;
  const {t} = useFormatters();
  const config = {
    ..._config,
    ui: {
      label: t(_config.ui.label),
      placeholder: _config.ui.placeholder ? t(_config.ui.placeholder) : _config.ui.placeholder,
      type: filterType,
    },
  };
  const [isOpened, setIsOpened] = useState(false);
  const toggleOpened = useCallback(() => {
    setIsOpened((v) => !v);
  }, []);
  const TextFilter = DynamicFilter.getFilterClass(filterType);
  const prevValue = useRef(value);
  const internalValue = useRef(value);
  const [_, setInternalValueNonce] = useState(0);
  const changeInternalValue = useCallback((newValue: string | null) => {
    internalValue.current = newValue;
    setInternalValueNonce((v) => v + 1);
  }, []);
  useEffect(() => {
    if (value !== prevValue.current) {
      prevValue.current = value;
      changeInternalValue(value ?? null);
    }
  }, [prevValue, value, changeInternalValue]);
  const filter = internalValue.current
    ? new TextFilter({
        type: filterType,
        property: config.name,
        value: internalValue.current,
      })
    : undefined;
  const onChangeDebounced = useMemo(
    () =>
      debounce(() => {
        if (onChange) onChange(internalValue.current ?? null);
      }, 400),
    [onChange]
  );
  const changeFilter = useCallback(
    (newFilter: Filter) => {
      const newValue = newFilter.getValue();
      changeInternalValue(newValue);
      onChangeDebounced();
    },
    [changeInternalValue, onChangeDebounced]
  );
  const removeFilter = useCallback(
    (newFilter: Filter) => {
      if (onChange) onChange(null);
    },
    [onChange]
  );
  const inputRef = useCallback(() => {
    // do nothing
  }, []);
  const setFocus = useCallback(() => 0, []);
  const node = new FilterNode(config);
  return (
    <span className={classNames(lightFilterNodeStyles.dlFilterNodeContent, 'dlFilterNode')} data-name={config.name}>
      {isTextArea ? (
        <FilterNodeTypeFulltext
          node={node}
          isOpened={isOpened}
          toggleOpened={toggleOpened}
          changeFilter={changeFilter}
          removeFilter={removeFilter}
          selectedFilter={filter}
          inputRef={inputRef}
          setFocus={setFocus}
          isMui
        />
      ) : (
        <FilterNodeTypeTextContainer
          node={node}
          isOpened={isOpened}
          toggleOpened={toggleOpened}
          changeFilter={changeFilter}
          removeFilter={removeFilter}
          selectedFilter={filter}
          inputRef={inputRef}
          setFocus={setFocus}
          wrapperWidth={wrapperWidth}
          inputWidth={inputWidth}
          iconName={iconName}
          isMui
        />
      )}
    </span>
  );
};
